/**
 * React hook pour consommer l'état de facturation et le droit de créer une
 * fiche. Unique point d'accès pour les composants UI — ne pas lire
 * `/users/{uid}/billing/current` directement ailleurs.
 *
 * Usage :
 *   const { billing, canCreateFiche, creditsRemaining, gatingReason, loading }
 *     = useEntitlements();
 *
 *   if (!canCreateFiche) return <CreditsExhaustedModal reason={gatingReason} />;
 *
 * Le hook est "safe" sans auth : quand `user` est null, il retourne
 * `billing: null`, `canCreateFiche: false`, `loading: false`. Les appels
 * Firestore ne démarrent qu'après login pour éviter les erreurs de règles.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  subscribeBilling,
  totalAvailableCredits,
  canCreateFiche as computeCanCreate,
  gatingReason as computeGating,
  freeCreditDaysLeft,
  summarizeCredits,
  type BillingDoc,
  type CreditsSummary,
  type GatingReason,
} from "./billing";

export type Entitlements = {
  /** Doc Firestore brut — null si non-chargé, non-authentifié, ou inexistant. */
  billing: BillingDoc | null;
  /** true = on attend la première lecture Firestore. */
  loading: boolean;
  /** true = l'utilisateur peut créer une fiche maintenant. */
  canCreateFiche: boolean;
  /** Total des crédits actionnables (inclus + top-ups + free credit). */
  creditsRemaining: number;
  /** Raison du blocage (affichée dans la modale d'upsell). null si OK. */
  gatingReason: GatingReason;
  /** Jours restants sur le crédit gratuit d'inscription. null si absent/expiré. */
  freeCreditDaysLeft: number | null;
  /** Détail des crédits par source (free / topup / plan) — utile pour la
   *  copy contextuelle (ex. sidebar, banners). */
  creditsSummary: CreditsSummary;
};

export function useEntitlements(): Entitlements {
  const { user, loading: authLoading } = useAuth();
  const [billing, setBilling] = useState<BillingDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setBilling(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeBilling(user.uid, (doc) => {
      setBilling(doc);
      setLoading(false);
    });
    return unsub;
  }, [user, authLoading]);

  return {
    billing,
    loading: authLoading || loading,
    canCreateFiche: computeCanCreate(billing),
    creditsRemaining: totalAvailableCredits(billing),
    gatingReason: computeGating(billing),
    freeCreditDaysLeft: freeCreditDaysLeft(billing),
    creditsSummary: summarizeCredits(billing),
  };
}
